// src/pages/Results2025.js - 2025 Race Results (Read-Only)
import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../utils/apiClient';

function Results2025() {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const { raceId } = useParams();
  
  const [races, setRaces] = useState([]);
  const [selectedRace, setSelectedRace] = useState(null);
  const [raceResults, setRaceResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState(null);

  // Fetch all completed races on mount
  useEffect(() => {
    const fetchRaces = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiClient.get('/api/f1data/races/all');
        
        if (!Array.isArray(response.data)) {
          setError('Invalid race data');
          setLoading(false);
          return;
        }

        // Filter for completed races and sort by date (most recent first)
        const now = new Date();
        const completedRaces = response.data
          .filter(race => 
            race && (
              race.status === 'completed' || 
              (race.date && new Date(race.date) < now)
            )
          )
          .sort((a, b) => {
            if (!a.date) return 1;
            if (!b.date) return -1;
            return new Date(b.date) - new Date(a.date);
          });

        setRaces(completedRaces);

        // If raceId is provided in URL, select that race
        if (raceId) {
          const race = completedRaces.find(r => r._id === raceId);
          if (race) {
            setSelectedRace(race);
            fetchRaceResults(race._id);
          }
        } else if (completedRaces.length > 0) {
          // Otherwise select the most recent race
          setSelectedRace(completedRaces[0]);
          fetchRaceResults(completedRaces[0]._id);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching races:', err);
        setError('Failed to load races');
      } finally {
        setLoading(false);
      }
    };

    fetchRaces();
  }, [raceId]);

  // Fetch results for a specific race
  const fetchRaceResults = async (raceId) => {
    try {
      setResultsLoading(true);
      setResultsError(null);
      
      const response = await apiClient.get(`/api/f1data/races/${raceId}/results`);
      setRaceResults(response.data);
      setResultsError(null);
    } catch (err) {
      console.error('Error fetching race results:', err);
      setResultsError('Failed to load race results');
      setRaceResults(null);
    } finally {
      setResultsLoading(false);
    }
  };

  // Handle race selection
  const handleRaceSelect = (race) => {
    setSelectedRace(race);
    fetchRaceResults(race._id);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">2025 Race Results</h1>
        <div className="text-center py-10">Loading races...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">2025 Race Results</h1>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">2025 Race Results</h1>
      <p className="text-gray-600 mb-6">Final results from completed 2025 season races</p>

      {/* Races List */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="md:col-span-1">
          <h2 className="text-xl font-bold mb-4">Races</h2>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              {races.length === 0 ? (
                <div className="p-4 text-gray-500 text-center">No completed races yet</div>
              ) : (
                races.map((race) => (
                  <button
                    key={race._id}
                    onClick={() => handleRaceSelect(race)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-200 hover:bg-blue-50 transition-colors ${
                      selectedRace?._id === race._id ? 'bg-blue-100 border-l-4 border-l-blue-600' : ''
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{race.raceName}</div>
                    <div className="text-sm text-gray-500">
                      {race.circuit?.country && `${race.circuit.country}`}
                      {race.date && ` • ${new Date(race.date).toLocaleDateString()}`}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Race Results */}
        <div className="md:col-span-2">
          {selectedRace ? (
            <div>
              <h2 className="text-xl font-bold mb-4">
                {selectedRace.raceName}
                {selectedRace.date && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    {new Date(selectedRace.date).toLocaleDateString()}
                  </span>
                )}
              </h2>

              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {resultsLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading results...</div>
                ) : resultsError ? (
                  <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
                    {resultsError}
                  </div>
                ) : raceResults ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Points</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {raceResults.results && raceResults.results.length > 0 ? (
                          raceResults.results.map((result, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="font-bold text-lg text-gray-900">
                                  {result.position || index + 1}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {result.driver?.name || result.driverName || 'Unknown'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {result.driver?.code || result.driverCode || ''}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {result.team?.name || result.teamName || 'Unknown'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                  {result.points || 0}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                              No results available for this race
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    Select a race to view results
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              No races available
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded">
        <h3 className="text-lg font-bold text-blue-900 mb-2">2025 Season Complete</h3>
        <p className="text-blue-800 mb-4">
          The 2025 season has concluded. This page shows the official F1 race results from all completed races.
        </p>
        <p className="text-blue-800">
          Ready for 2026? Head to <strong>2026 Season → Predictions</strong> to start making your predictions for the new season!
        </p>
      </div>
    </div>
  );
}

export default Results2025;